import React from 'react';
import { useLocation } from 'react-router-dom';
import { Lightbulb } from 'lucide-react';
import { guideApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { GuideModale, type VueGuide } from './GuideModale';
import { moduleDuChemin, type DonneesGuide } from './guideTypes';

/**
 * AIDE GUIDÉE — l'orchestrateur (design/CHANTIER-AIDE-GUIDEE.md, lot 1).
 *
 * Un seul composant tient l'état, la fenêtre et l'ampoule : le guide n'a qu'un point d'entrée dans
 * l'application, un contexte React aurait été une cérémonie pour rien.
 *
 * CE QUI S'OUVRE TOUT SEUL, ET QUAND — c'est la règle du § 2 du chantier :
 *  - jamais suivi la visite            → « Bienvenue », avec Continuer / Plus tard ;
 *  - déjà suivie, une version publiée  → SEULEMENT ce qui a changé ;
 *  - sinon                             → rien, l'ampoule attend qu'on la sollicite.
 *
 * Réservé aux comptes `client` et `collaborator` : ni les salariés, ni l'admin (décision d'Emeni).
 */
/**
 * MISE DE COTÉ, SUR DEMANDE D'EMENI (20/09/2026). L'aide guidée est terminée et fonctionne — socle,
 * écran de rédaction, publication d'annonces, chapitres Menus & Convives et Mode terrain — mais elle ne
 * doit pas apparaître chez les clients pour l'instant.
 *
 * Un seul interrupteur, ici : le passer à `true` fait revenir l'ampoule et sa fenêtre, sans autre
 * modification. Rien n'est supprimé, les routes et l'onglet de rédaction restent en place, et le contenu
 * déjà écrit attend en base.
 */
const AIDE_GUIDEE_VISIBLE = false;

export function AideGuidee() {
    const { user } = useAuth();
    const location = useLocation();
    const [donnees, setDonnees] = React.useState<DonneesGuide | null>(null);
    const [vue, setVue] = React.useState<VueGuide | null>(null);
    const [index, setIndex] = React.useState(0);
    // L'ouverture automatique n'a lieu qu'une fois par session : sans ce verrou, revenir sur une page
    // après avoir fermé la fenêtre la rouvrirait aussitôt.
    const auto = React.useRef(false);

    const concerne = AIDE_GUIDEE_VISIBLE && (user?.type === 'client' || user?.type === 'collaborator');

    React.useEffect(() => {
        if (!concerne) { setDonnees(null); return; }
        let annule = false;
        guideApi.get()
            .then((r: any) => { if (!annule) setDonnees(r && Array.isArray(r.chapitres) ? r : null); })
            .catch(() => { /* l'aide n'est jamais bloquante : en cas d'échec, l'ampoule ne s'affiche pas */ });
        return () => { annule = true; };
    }, [concerne, user?.id]);

    React.useEffect(() => {
        if (!donnees || auto.current || vue) return;
        const etat = donnees.etat;
        if (!etat) return;
        auto.current = true;
        if (!etat.formation_vue && donnees.chapitres.length > 0) { setVue('bienvenue'); return; }
        const v = donnees.version;
        if (v && v.id !== etat.version_masquee_id) setVue('nouveautes');
    }, [donnees, vue]);

    if (!concerne || !donnees || donnees.chapitres.length === 0) return null;

    const chapitres = donnees.chapitres;
    const moduleCourant = moduleDuChemin(location.pathname);

    const fermer = () => setVue(null);

    const commencer = () => { setIndex(0); setVue('visite'); };

    const ouvrirChapitre = (i: number) => { setIndex(i); setVue('visite'); };

    // Terminer la visite pose le repère qui fait qu'on ne la repropose plus d'office. On ferme sans
    // attendre la réponse du serveur : faire patienter quelqu'un devant un « Terminer » serait absurde,
    // et un échec d'écriture ne coûte qu'une proposition de trop à la prochaine session.
    const terminer = () => {
        fermer();
        setDonnees((d) => (d && d.etat ? { ...d, etat: { ...d.etat, formation_vue: true } } : d));
        guideApi.formationVue().catch(() => { /* sans conséquence */ });
    };

    const nePlusAfficher = () => {
        const versionId = donnees.version?.id || null;
        fermer();
        setDonnees((d) => (d && d.etat ? { ...d, etat: { ...d.etat, version_masquee_id: versionId } } : d));
        guideApi.masquerVersion(versionId).catch(() => { /* sans conséquence */ });
    };

    return (
        <>
            {/* L'ampoule, toujours là. En bas à DROITE : la bulle ToDo occupe déjà le coin gauche. */}
            <button type="button" onClick={() => setVue('sommaire')}
                title="Aide : à quoi sert chaque module, et comment l'utiliser"
                aria-label="Ouvrir l'aide"
                className="fixed bottom-5 right-5 z-[9998] w-12 h-12 rounded-full bg-surface border border-border shadow-lg text-content flex items-center justify-center hover:bg-surface-soft hover:border-content/30 transition-colors">
                <Lightbulb size={20} />
            </button>

            {vue && (
                <GuideModale
                    vue={vue}
                    chapitres={chapitres}
                    version={donnees.version}
                    index={index}
                    moduleCourant={moduleCourant}
                    onFermer={fermer}
                    onCommencer={commencer}
                    onOuvrirSommaire={() => setVue('sommaire')}
                    onOuvrirChapitre={ouvrirChapitre}
                    onAller={setIndex}
                    onTerminer={terminer}
                    onNePlusAfficher={nePlusAfficher}
                />
            )}
        </>
    );
}
